/** The plan editor's view of the plan document: rows, groups, drafts and the human summary of an edit. */
import { contract, formatDay, parseDay, type Day } from '@map-my-money/engine';
import type { WireAsset, WirePlan, WireTransaction } from '../api/types.ts';
import { t, type MessageKey } from '../i18n/index.ts';
import { fullDate, money, monthYear } from './format.ts';
import { datedFieldsOf, historyLines, itemOn, type DatedField, type HistoryLine, type ItemFacts, type RateScheduleView } from './model.ts';

export type Group = 'income' | 'spending' | 'investing' | 'debts' | 'assets';
export const GROUPS: readonly { key: Group; label: MessageKey }[] = [
  { key: 'income', label: 'plan.filter.income' },
  { key: 'spending', label: 'plan.filter.spending' },
  { key: 'investing', label: 'plan.filter.investing' },
  { key: 'debts', label: 'plan.filter.debts' },
  { key: 'assets', label: 'plan.filter.assets' },
];

export type ItemKey = `tx:${string}` | `asset:${string}`;

export type PlanItem =
  | { readonly key: ItemKey; readonly kind: 'tx'; readonly group: Group; readonly tx: WireTransaction }
  | { readonly key: ItemKey; readonly kind: 'asset'; readonly group: Group; readonly asset: WireAsset };

const TX_GROUP: Record<WireTransaction['type'], Group> = {
  income: 'income', expense: 'spending', investment: 'investing', debt: 'debts', property: 'assets', depreciatingAsset: 'assets',
};

export function planItems(wire: WirePlan): PlanItem[] {
  const txs: PlanItem[] = (wire.transactions ?? []).map((tx) => ({ key: `tx:${tx.id}`, kind: 'tx', group: TX_GROUP[tx.type], tx }));
  const assets: PlanItem[] = (wire.assets ?? []).map((asset) => ({ key: `asset:${asset.id}`, kind: 'asset', group: 'assets', asset }));
  return [...txs, ...assets];
}

export const itemLabel = (item: PlanItem) => (item.kind === 'tx' ? item.tx.label ?? item.tx.id : item.asset.label ?? item.asset.id);
export const itemActive = (item: PlanItem) => (item.kind === 'tx' ? item.tx.isActive !== false : item.asset.isActive !== false);

export function typeLabel(item: PlanItem): string {
  if (item.kind === 'asset') {
    return item.asset.assetClass === 'property' ? t('plan.type.property') : item.asset.assetClass === 'vehicle' ? t('plan.type.vehicle') : t('plan.type.asset');
  }
  return t(`plan.type.${item.tx.type === 'property' || item.tx.type === 'depreciatingAsset' ? 'asset' : item.tx.type}` as MessageKey);
}

export const TYPE_TONE: Record<string, string> = {
  income: 'mint', expense: 'amber', investment: 'violet', debt: 'terracotta', asset: 'violet',
};
export const itemTone = (item: PlanItem) => (item.kind === 'asset' ? 'violet' : TYPE_TONE[item.tx.type] ?? 'slate');

export function frequencyLabel(item: PlanItem): string {
  if (item.kind === 'asset') return t('plan.freq.value');
  const f = item.tx.frequency ?? 'monthly';
  return typeof f === 'string' ? t(`plan.freq.${f}` as MessageKey) : t('plan.freq.custom');
}

const perLabel = (tx: WireTransaction) => {
  const f = tx.frequency ?? 'monthly';
  if (f === 'monthly') return t('common.perMonth');
  if (f === 'yearly') return t('common.perYear');
  return '';
};
export const amountWithFrequency = (tx: WireTransaction) => `${money(Number(tx.amount))}${perLabel(tx)}`;

export interface RowCells {
  readonly amount: number;
  readonly starts: Day;
  readonly ends: Day | null;
  readonly endsIsPayoff: boolean;
  readonly grows: string | null;
  /** An asset bought after today: shown as planned, not as owned (R23). */
  readonly plannedFrom: Day | null;
  /** The next change still to come (R24); `amount` is null when it changes something else. */
  readonly next: { readonly from: Day; readonly amount: number | null } | null;
}

/** A table row's cells: the values in force `today`; `facts` are the item's numbers today from `planFacts`. */
export function rowCells(item: PlanItem, facts: ItemFacts | null | undefined, today: string): RowCells {
  if (item.kind === 'asset') {
    const a = contract.decodeAsset(item.asset);
    const traj = item.asset.valueTrajectory;
    return {
      amount: facts?.kind === 'asset' ? facts.valueToday : Number(item.asset.purchasePrice),
      starts: a.purchaseDate,
      ends: a.saleEvent?.date ?? null,
      endsIsPayoff: false,
      grows: traj.kind === 'appreciating' ? t('plan.grows.pct', { pct: traj.annualRate }) : traj.kind === 'depreciating' ? t('plan.grows.depreciating') : null,
      plannedFrom: facts?.kind === 'asset' ? facts.plannedFrom : null,
      next: null,
    };
  }
  const tx = contract.decodeTransaction(item.tx);
  const isDebt = tx.type === 'debt';
  const current = itemOn(item.tx, today);
  let grows: string | null = null;
  if (tx.type === 'investment') {
    grows = current.employerMatch && Number(current.employerMatch) > 0 ? t('plan.grows.match', { pct: current.employerMatch }) : current.interestRate ? t('plan.grows.pct', { pct: current.interestRate }) : null;
  } else if (!isDebt && Number(current.annualIncreasePercent ?? 0) > 0) {
    grows = t('plan.grows.pct', { pct: current.annualIncreasePercent! });
  }
  const upcoming = historyLines(item.tx, today).find((line) => line.when === 'future' && line.from !== null);
  return {
    amount: Number(current.amount),
    starts: tx.startDate,
    ends: isDebt ? (facts?.kind === 'debt' ? facts.payoff : null) : tx.endDate,
    endsIsPayoff: isDebt,
    grows,
    plannedFrom: null,
    next: upcoming ? { from: parseDay(upcoming.from!), amount: upcoming.changed.includes('amount') ? Number(upcoming.values.amount) : null } : null,
  };
}

/** "→ £1,400 from Nov 2026" */
export const nextChangeText = (next: NonNullable<RowCells['next']>) =>
  next.amount === null ? t('plan.from.nextOther', { month: monthYear(next.from) }) : t('plan.from.next', { amount: money(next.amount), month: monthYear(next.from) });

// MARK: Drafts

export interface RateRow {
  readonly date: string;
  readonly rate: string;
}

export interface Draft {
  label: string;
  amount: string;
  frequency: string;
  startDate: string;
  endDate: string;
  growth: string;
  rate: string;
  initialBalance: string;
  employerMatch: string;
  /** A debt entered by its balance: the balance and the day it was true. */
  balance: string;
  balanceDate: string;
  rateChanges: readonly RateRow[];
  /** The income the employer match follows; '' for the largest. */
  matchIncomeId: string;
  taxAccount: string;
  taxRelief: string;
  isActive: boolean;
}

const NO_EXTRAS = { balance: '', balanceDate: '', rateChanges: [], matchIncomeId: '', taxAccount: '', taxRelief: '' } as const;

const absString = (value: string | undefined) => (value === undefined ? '' : String(Math.abs(Number(value))));

/**
 * The editor's fields for an item. `facts` supply what the stored item leaves to the plan: a debt
 * entered by its balance without a day of its own shows the day the engine gives it (the setup day).
 */
export function draftFrom(item: PlanItem, facts?: ItemFacts | null): Draft {
  if (item.kind === 'asset') {
    const a = item.asset;
    return {
      label: a.label ?? '', amount: a.purchasePrice, frequency: 'value', startDate: a.purchaseDate, endDate: a.saleEvent?.date ?? '',
      growth: a.valueTrajectory.kind === 'appreciating' ? a.valueTrajectory.annualRate : '', rate: '', initialBalance: '', employerMatch: '',
      ...NO_EXTRAS, isActive: a.isActive !== false,
    };
  }
  const tx = item.tx;
  const byBalance = tx.type === 'debt' && tx.debtEntryMode === 'currentBalance';
  const balanceDay = facts?.kind === 'debt' && facts.balanceDate !== null ? formatDay(facts.balanceDate) : '';
  return {
    label: tx.label ?? '', amount: tx.amount, frequency: typeof tx.frequency === 'string' ? tx.frequency : tx.frequency ? 'custom' : 'monthly',
    startDate: tx.startDate, endDate: tx.endDate ?? '', growth: tx.annualIncreasePercent ?? '', rate: tx.interestRate ?? '',
    initialBalance: tx.initialBalance ?? '', employerMatch: tx.employerMatch ?? '',
    balance: byBalance ? absString(tx.debtCurrentBalance) : '',
    balanceDate: byBalance ? tx.debtBalanceDate ?? balanceDay : '',
    rateChanges: [...(tx.interestRateChanges ?? [])].sort((a, b) => a.date.localeCompare(b.date)).map((c) => ({ date: c.date, rate: c.newRate })),
    matchIncomeId: tx.employerMatchIncomeId ?? '',
    taxAccount: tx.taxRelief?.accountType ?? '',
    taxRelief: tx.taxRelief?.reliefPercentage ?? '',
    isActive: tx.isActive !== false,
  };
}

export interface RateRowError {
  readonly date?: string;
  readonly rate?: string;
}

export type DraftErrors = Partial<Record<Exclude<keyof Draft, 'rateChanges'>, string>> & { rateChanges?: readonly (RateRowError | null)[] };

const isNumber = (s: string) => s.trim() !== '' && Number.isFinite(Number(s));
const clean = (s: string) => s.replace(/[£$,\s%]/g, '');
const isDay = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);

function rateRowErrors(draft: Draft): (RateRowError | null)[] | null {
  const rows = draft.rateChanges.map((row, i): RateRowError | null => {
    const error: { date?: string; rate?: string } = {};
    if (!isDay(row.date)) error.date = t('plan.field.required');
    else if (isDay(draft.startDate) && row.date <= draft.startDate) error.date = t('plan.rates.afterStart');
    else if (draft.rateChanges.findIndex((other) => other.date === row.date) !== i) error.date = t('plan.rates.duplicate');
    const rate = clean(row.rate);
    if (!isNumber(rate)) error.rate = t('plan.field.number');
    else if (Number(rate) < 0) error.rate = t('plan.field.positive');
    return error.date || error.rate ? error : null;
  });
  return rows.some((r) => r !== null) ? rows : null;
}

/** What is wrong with the draft; `today` (YYYY-MM-DD) refuses a balance dated after it. */
export function validate(draft: Draft, today?: string): DraftErrors {
  const errors: DraftErrors = {};
  if (!draft.label.trim()) errors.label = t('plan.field.required');
  const amount = clean(draft.amount);
  if (!isNumber(amount)) errors.amount = t('plan.field.number');
  else if (Number(amount) < 0) errors.amount = t('plan.field.positive');
  if (!isDay(draft.startDate)) errors.startDate = t('plan.field.required');
  if (draft.endDate && draft.startDate && draft.endDate <= draft.startDate) errors.endDate = t('plan.field.endAfterStart');
  for (const key of ['growth', 'rate', 'initialBalance', 'employerMatch', 'balance', 'taxRelief'] as const) {
    const v = clean(draft[key]);
    if (v !== '' && !isNumber(v)) errors[key] = t('plan.field.number');
    else if (v !== '' && Number(v) < 0) errors[key] = t('plan.field.positive');
  }
  if (!errors.taxRelief && clean(draft.taxRelief) !== '' && Number(clean(draft.taxRelief)) > 100) errors.taxRelief = t('plan.field.atMost100');
  if (draft.balanceDate !== '' && !isDay(draft.balanceDate)) errors.balanceDate = t('plan.field.required');
  else if (today && draft.balanceDate > today) errors.balanceDate = t('plan.field.notFuture');
  const rows = rateRowErrors(draft);
  if (rows) errors.rateChanges = rows;
  return errors;
}

const numString = (s: string) => String(Number(clean(s)));

/** The item with the draft's edits applied; fields the user didn't touch are left exactly as stored. */
export function applyDraft(item: PlanItem, draft: Draft, facts?: ItemFacts | null): PlanItem {
  const base = draftFrom(item, facts);
  const changed = (key: keyof Draft) => JSON.stringify(draft[key]) !== JSON.stringify(base[key]);
  const next: Record<string, unknown> = { ...(item.kind === 'tx' ? item.tx : item.asset) };
  const setNumber = (field: string, value: string) => {
    if (clean(value) === '') delete next[field];
    else next[field] = numString(value);
  };
  const setText = (field: string, value: string) => {
    if (value === '') delete next[field];
    else next[field] = value;
  };
  if (changed('label')) next.label = draft.label.trim();
  if (changed('isActive')) next.isActive = draft.isActive;
  if (item.kind === 'asset') {
    if (changed('amount')) next.purchasePrice = numString(draft.amount);
    if (changed('startDate')) next.purchaseDate = draft.startDate;
    if (changed('growth') && item.asset.valueTrajectory.kind === 'appreciating' && draft.growth !== '') {
      next.valueTrajectory = { kind: 'appreciating', annualRate: numString(draft.growth) };
    }
    return { ...item, asset: next as unknown as WireAsset };
  }
  const type = item.tx.type;
  if (changed('amount')) next.amount = numString(draft.amount);
  if (changed('startDate')) next.startDate = draft.startDate;
  if (changed('frequency') && draft.frequency !== 'custom') next.frequency = draft.frequency;
  if (changed('endDate')) setText('endDate', draft.endDate);
  if (changed('growth') && (type === 'income' || type === 'expense')) setNumber('annualIncreasePercent', draft.growth);
  if (changed('rate') && (type === 'investment' || type === 'debt')) setNumber('interestRate', draft.rate);
  if (changed('initialBalance') && type === 'investment') setNumber('initialBalance', draft.initialBalance);
  if (changed('employerMatch') && type === 'investment') setNumber('employerMatch', draft.employerMatch);
  if (type === 'debt' && item.tx.debtEntryMode === 'currentBalance') {
    if (changed('balance')) setNumber('debtCurrentBalance', draft.balance);
    if (changed('balanceDate')) setText('debtBalanceDate', draft.balanceDate);
  }
  if (type === 'debt' && changed('rateChanges')) {
    const rows = [...draft.rateChanges].sort((a, b) => a.date.localeCompare(b.date)).map((r) => ({ date: r.date, newRate: numString(r.rate) }));
    if (rows.length === 0) delete next.interestRateChanges;
    else next.interestRateChanges = rows;
  }
  if (type === 'investment') {
    if (changed('matchIncomeId')) setText('employerMatchIncomeId', draft.matchIncomeId);
    if (changed('taxAccount') || changed('taxRelief')) {
      if (draft.taxAccount === '') delete next.taxRelief;
      else next.taxRelief = { accountType: draft.taxAccount, ...(clean(draft.taxRelief) === '' ? {} : { reliefPercentage: numString(draft.taxRelief) }) };
    }
  }
  return { ...item, tx: next as unknown as WireTransaction };
}

// MARK: Dated edits (R24)

export interface DatedEdit {
  readonly field: DatedField;
  readonly before: string | null;
  readonly after: string | null;
}

const datedValue = (tx: WireTransaction, field: DatedField): string | null =>
  tx[field] ?? (field === 'annualIncreasePercent' ? '0' : null);

/** The dated fields an edit changes on a stored transaction; a debt's rate is dated by its rate schedule instead. */
export function datedEdits(item: PlanItem, after: PlanItem): DatedEdit[] {
  if (item.kind !== 'tx' || after.kind !== 'tx') return [];
  return datedFieldsOf(item.tx)
    .map((field) => ({ field, before: datedValue(item.tx, field), after: datedValue(after.tx, field) }))
    .filter((e) => (e.before === null || e.after === null ? e.before !== e.after : Number(e.before) !== Number(e.after)));
}

const valueText = (field: DatedField, value: string | null) =>
  field === 'amount' ? money(Number(value ?? 0)) : t(`plan.from.value.${field}` as MessageKey, { pct: value ?? '0' });

/** "£1,200 → £1,400, yearly rise 3% → 4%" */
export const datedEditsText = (edits: readonly DatedEdit[]) =>
  edits
    .map((e) => (e.field === 'amount'
      ? t('plan.from.change.amount', { from: valueText('amount', e.before), to: valueText('amount', e.after) })
      : t(`plan.from.change.${e.field}` as MessageKey, { from: e.before ?? '0', to: e.after ?? '0' })))
    .join(', ');

/** "£1,400, rising 4% a year": what a correction says it always was. */
export const correctedText = (edits: readonly DatedEdit[]) => edits.map((e) => valueText(e.field, e.after)).join(', ');

/** What is wrong with a From day; `earliest` is the day of a change still to come, which an edit cannot go before. */
export function fromError(from: string, earliest: string | null): string | null {
  if (!isDay(from)) return t('plan.field.required');
  if (earliest !== null && from < earliest) return t('plan.from.afterChange', { date: fullDate(parseDay(earliest)) });
  return null;
}

/** A history stretch for the editor: "£1,000/mo until 10 Oct 2026", "£1,400/mo from 1 Nov 2026". */
export function historyLineText(tx: WireTransaction, line: HistoryLine, shown: readonly DatedField[]): string {
  const values = [
    `${money(Number(line.values.amount ?? 0))}${perLabel(tx)}`,
    ...shown.filter((f) => f !== 'amount').map((f) => valueText(f, line.values[f])),
  ].join(', ');
  const from = line.from === null ? null : fullDate(parseDay(line.from));
  const until = line.until === null ? null : fullDate(parseDay(line.until));
  if (from === null) return until === null ? values : t('plan.history.until', { values, date: until });
  if (until !== null) return t('plan.history.between', { values, from, until });
  return t(line.when === 'future' ? 'plan.history.from' : 'plan.history.since', { values, date: from });
}

/** The dated fields that differ anywhere in an item's history, amount first. */
export const historyFields = (lines: readonly HistoryLine[]): DatedField[] => {
  const changed = new Set(lines.flatMap((l) => l.changed));
  return (['amount', 'annualIncreasePercent', 'interestRate', 'employerMatch'] as const).filter((f) => f === 'amount' || changed.has(f));
};

export function replaceItem(wire: WirePlan, item: PlanItem): WirePlan {
  if (item.kind === 'asset') {
    const assets = wire.assets ?? [];
    const exists = assets.some((a) => a.id === item.asset.id);
    return { ...wire, assets: exists ? assets.map((a) => (a.id === item.asset.id ? item.asset : a)) : [...assets, item.asset] };
  }
  const txs = wire.transactions ?? [];
  const exists = txs.some((x) => x.id === item.tx.id);
  return { ...wire, transactions: exists ? txs.map((x) => (x.id === item.tx.id ? item.tx : x)) : [...txs, item.tx] };
}

export function removeItem(wire: WirePlan, item: PlanItem): WirePlan {
  if (item.kind === 'asset') {
    return {
      ...wire,
      assets: (wire.assets ?? []).filter((a) => a.id !== item.asset.id),
      transactions: (wire.transactions ?? []).map((x) => {
        if (x.linkedAssetId !== item.asset.id) return x;
        const { linkedAssetId: _unlinked, ...rest } = x;
        return rest;
      }),
    };
  }
  const removed = item.tx.id;
  return {
    ...wire,
    transactions: (wire.transactions ?? []).filter((x) => x.id !== removed).map((x) => {
      if (x.employerMatchIncomeId !== removed) return x;
      const { employerMatchIncomeId: _unlinked, ...rest } = x;
      return rest;
    }),
  };
}

const FIELD_NAMES: Record<string, string> = {
  interestRate: 'rate', annualIncreasePercent: 'growth', startDate: 'start', endDate: 'end', initialBalance: 'balance',
  employerMatch: 'employer match', frequency: 'frequency', purchasePrice: 'price', purchaseDate: 'bought', label: 'name',
};

function fieldValue(field: string, value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (field === 'interestRate' || field === 'annualIncreasePercent' || field === 'employerMatch') return `${value}%`;
  if (field === 'startDate' || field === 'endDate' || field === 'purchaseDate') return monthYear(parseDay(String(value)));
  if (field === 'initialBalance' || field === 'purchasePrice') return money(Number(value));
  return String(value);
}

/** "5.4% from Apr 2027, 6% from Jan 2030" */
const rateChangesText = (changes: readonly { date: string; newRate: string }[]) =>
  changes.map((c) => t('plan.rates.from', { rate: c.newRate, month: monthYear(parseDay(c.date)) })).join(', ');

/** "4.1%, then 5.4% from Apr 2027 · payment rises £124 a month from Apr 2027"; a card: "… · payment stays £200, paid off Feb 2030". */
export function rateScheduleText(view: RateScheduleView): string {
  const rates = view.changes.reduce(
    (text, c) => t('plan.rates.then', { rates: text, next: t('plan.rates.from', { rate: c.rate, month: monthYear(c.date) }) }),
    t('plan.rates.base', { rate: view.baseRate }),
  );
  const payment = view.paymentChanges.length > 0
    ? view.paymentChanges
      .map((p) => t(p.change > 0 ? 'plan.rates.paymentRises' : 'plan.rates.paymentFalls', { amount: money(Math.abs(p.change)), month: monthYear(p.from) }))
      .join(', ')
    : view.payoff === null
      ? t('plan.rates.paymentStaysNever', { amount: money(view.payment) })
      : t('plan.rates.paymentStays', { amount: money(view.payment), date: monthYear(view.payoff) });
  return `${rates} · ${payment}`;
}

export const accountLabel = (account: string) => t(`plan.tax.${account}` as MessageKey);

/** The human summary of edits to the newer R23 fields (a debt's dated balance, its rates, whose pay a match follows, tax relief). */
function timelineSummary(name: string, changed: readonly string[], b: WireTransaction, wire: WirePlan | undefined): string | null {
  const only = (...fields: string[]) => changed.length > 0 && changed.every((f) => fields.includes(f));
  if (only('debtCurrentBalance', 'debtBalanceDate') && b.debtCurrentBalance !== undefined) {
    const date = b.debtBalanceDate ?? wire?.configuration.initialBalanceDate;
    return date
      ? t('plan.summaryText.balance', { name, amount: money(Math.abs(Number(b.debtCurrentBalance))), date: fullDate(parseDay(date)) })
      : t('plan.summaryText.balanceUndated', { name, amount: money(Math.abs(Number(b.debtCurrentBalance))) });
  }
  if (only('interestRateChanges')) {
    const changes = b.interestRateChanges ?? [];
    return changes.length === 0 ? t('plan.summaryText.ratesCleared', { name }) : t('plan.summaryText.rates', { name, rates: rateChangesText(changes) });
  }
  if (only('employerMatchIncomeId')) {
    const income = (wire?.transactions ?? []).find((x) => x.id === b.employerMatchIncomeId);
    return income ? t('plan.summaryText.matchIncome', { name, income: income.label ?? income.id }) : t('plan.summaryText.matchLargest', { name });
  }
  if (only('taxRelief')) {
    if (!b.taxRelief) return t('plan.summaryText.taxReliefCleared', { name });
    const account = accountLabel(b.taxRelief.accountType);
    return b.taxRelief.reliefPercentage === undefined
      ? t('plan.summaryText.taxRelief', { name, account })
      : t('plan.summaryText.taxReliefPct', { name, account, pct: b.taxRelief.reliefPercentage });
  }
  return null;
}

/**
 * "Salary → £4,600/mo", "Car loan rate 6.9% → 6.5%", "Card balance £1,950 on 11 Oct 2026", "Updated Holiday";
 * `dated` adds the From day of a dated edit when it is not today ("Rent → £1,400/mo from 1 Nov 2026").
 */
export function editSummary(before: PlanItem, after: PlanItem, wire?: WirePlan, dated?: { readonly from: string; readonly today: string }): string {
  const summary = undatedSummary(before, after, wire);
  if (!dated || dated.from === dated.today || after.kind !== 'tx' || datedEdits(before, after).length === 0) return summary;
  return dated.from <= after.tx.startDate
    ? t('plan.from.summaryStart', { summary })
    : t('plan.from.summaryOn', { summary, date: fullDate(parseDay(dated.from)) });
}

function undatedSummary(before: PlanItem, after: PlanItem, wire?: WirePlan): string {
  const a = (before.kind === 'tx' ? before.tx : before.asset) as unknown as Record<string, unknown>;
  const b = (after.kind === 'tx' ? after.tx : after.asset) as unknown as Record<string, unknown>;
  const changed = [...new Set([...Object.keys(a), ...Object.keys(b)])].filter((k) => JSON.stringify(a[k]) !== JSON.stringify(b[k]));
  const name = itemLabel(after);
  if (changed.length === 1 && changed[0] === 'amount' && after.kind === 'tx') return t('plan.summaryText.amount', { name, amount: amountWithFrequency(after.tx) });
  if (changed.length === 1 && changed[0] === 'isActive') return t(b.isActive === false ? 'plan.summaryText.paused' : 'plan.summaryText.resumed', { name });
  const timeline = after.kind === 'tx' ? timelineSummary(name, changed, after.tx, wire) : null;
  if (timeline !== null) return timeline;
  if (changed.length === 1 && changed[0] !== undefined) {
    const field = changed[0];
    return t('plan.summaryText.changed', { name: itemLabel(before), field: FIELD_NAMES[field] ?? field, from: fieldValue(field, a[field]), to: fieldValue(field, b[field]) });
  }
  return t('plan.summaryText.updated', { name });
}

export const addSummary = (item: PlanItem) =>
  t('plan.summaryText.added', { name: itemLabel(item), amount: item.kind === 'tx' ? amountWithFrequency(item.tx) : money(Number(item.asset.purchasePrice)) });

export type NewKind = 'income' | 'expense' | 'investment' | 'debt' | 'asset';

export function newItem(kind: NewKind, today: string, wire: WirePlan): PlanItem {
  const id = `${kind}-${Date.now().toString(36)}`;
  const label = t('plan.new', { type: t(kind === 'asset' ? 'plan.type.asset' : (`plan.newItem.${kind}` as MessageKey)).toLowerCase() });
  if (kind === 'asset') {
    return {
      key: `asset:${id}`, kind: 'asset', group: 'assets',
      asset: { id, label, purchasePrice: '10000', purchaseDate: today, assetClass: 'other', valueTrajectory: { kind: 'stable' } },
    };
  }
  const base: WireTransaction = { id, label, type: kind, amount: kind === 'income' ? '500' : '100', startDate: today };
  const tx: WireTransaction =
    kind === 'investment' ? { ...base, initialBalance: '0', interestRate: wire.configuration.expectedReturnPercent }
    : kind === 'debt' ? { ...base, amount: '150', debtType: 'openEnded', debtEntryMode: 'currentBalance', debtCurrentBalance: '2000', debtBalanceDate: today, interestRate: '20' }
    : base;
  return { key: `tx:${id}`, kind: 'tx', group: TX_GROUP[kind], tx };
}
